/**
 * Support page user guide: Arie chat, emailing Arie, and the automatic tools.
 * Plain content only. No real mailbox address appears here, because every
 * company emails its own shared mailbox (shown in Agent activity → Settings).
 */
import React, { useState } from 'react';

interface Topic {
  title: string;
  points: string[];
}

interface Guide {
  id: string;
  label: string;
  intro: string;
  topics: Topic[];
}

interface ToolGuide {
  name: string;
  dropIn: string;
  picks: string[];
}

const ARIE_CHAT: Guide = {
  id: 'chat',
  label: 'Arie chat',
  intro: 'Arie is the assistant behind the ninja button in the bottom corner of every page. Talk to her in plain English. She knows which customer, folder or report you have open.',
  topics: [
    {
      title: 'Ask questions',
      points: [
        'Coverage questions about a document or report: "Does the Acme GL policy have a contractual liability exclusion?" She reads the document and points you to where she found the answer.',
        'Find things: "Which customers have a policy expiring in the next 60 days?", "Show me the latest loss run for Bluefin."',
        'Agency know-how: "How do we handle a late notice of claim?" She searches your agency Knowledge Base (Library → Knowledge Base) and names the documents she used.',
      ],
    },
    {
      title: 'File new documents',
      points: [
        'Drag files into the Arie window. She works out which customer and folder they belong to, suggests names, and shows you what she plans to do before anything is saved.',
        'She never creates a duplicate customer. If she cannot find a match, she asks before creating a new one.',
      ],
    },
    {
      title: 'Start analyses',
      points: [
        'Ask for the outcome you want, such as "compare the 2025 and 2026 cyber policies" or "benchmark the BOP". She picks the right tool and documents and shows you a card to confirm.',
        'Reports run in the background. Ask "is my report done?" or watch the bell.',
      ],
    },
    {
      title: 'Write things for you',
      points: [
        'Plain-language summaries for the insured, a risk narrative for the broker, a coverage explainer, or a short email draft.',
        'Turn a finished report into a client-ready PDF: "Summarize this report into a PDF for the client covering the key findings."',
      ],
    },
    {
      title: 'Tidy up and plan ahead',
      points: [
        'Create, rename and move folders and documents, or update customer details.',
        'Several steps at once: "rename this document, then move it into the renewal folder" becomes one plan you approve once.',
        'Standing tasks: "when the extractions in this folder finish, run the loss run analysis" or "every Monday, check for renewal-ready folders."',
      ],
    },
    {
      title: 'You stay in control',
      points: [
        'Anything that changes your data appears as a card. Nothing happens until you click to confirm.',
        'Arie never emails anyone. Emails she writes for clients or carriers are drafts for you to send.',
      ],
    },
  ],
};

const EMAIL: Guide = {
  id: 'email',
  label: 'Emailing Arie',
  intro: "Each company has its own Arie mailbox. Find yours under Agent activity → Settings (copy it into your email contacts). Send to it from the email address on your RiskNinja account. Your company admin switches the agent on.",
  topics: [
    {
      title: 'Send her work',
      points: [
        'Send a new email, or forward one you received, to your Arie mailbox.',
        'Write what you want at the top, in your own words: "Here are Acme\'s renewal docs, run the year-over-year", "Compare these two quotes", "Draft a reply to the underwriter".',
        'Arie reads the whole email chain, files the attachments into the right customer and folder, runs the work, and emails you a summary with links and the finished reports attached.',
        'Messages can be up to 150 MB. Larger files should be uploaded in the app.',
      ],
    },
    {
      title: 'Sending from another address',
      points: [
        'Arie only accepts email from people she knows. If you send from an address that is not your login email, add it under Agent activity → Settings → Forwarding addresses and click the link in the verification email.',
        'Email from an address Arie does not recognize is not processed.',
      ],
    },
    {
      title: 'Keep going by replying',
      points: [
        'Reply to Arie\'s summary to continue the same request, for example to send a document she said was missing. She picks up where she left off.',
        'If something needs your OK (such as creating a new customer), the summary says so. Approve it under Agent activity → Approvals. When only one item is waiting, you can also reply "approve" or "reject".',
      ],
    },
    {
      title: 'Copying colleagues',
      points: [
        'CC colleagues from your company on your email to Arie and they get a copy of her final summary.',
        'A colleague is copied only if the address you CC is verified under their own Forwarding addresses. Clients and anyone outside your company are never copied.',
        'The work is done in your account. If a colleague replies, Arie starts a new request in their account.',
      ],
    },
    {
      title: 'Fill in a template',
      points: [
        'Attach a blank application, supplemental, questionnaire or schedule and ask Arie to fill it in for a customer.',
        'Word, Excel and fillable PDF files work. Scanned or flat PDFs do not; Arie tells you if she cannot use a template.',
        'She fills only what your email, the customer\'s records or their documents support. Everything else is left blank, and her summary lists the important blanks.',
        'The filled copy is saved in the customer\'s folder and attached to her reply, in the same format you sent.',
      ],
    },
    {
      title: 'Limits and safety',
      points: [
        'One email can start up to 3 analyses. If you list more (for example several new-business prospects), Arie starts the first 3 in your order and lists the rest. Reply with the ones you want next.',
        'Arie never emails clients, carriers or anyone outside RiskNinja. Replies she writes for them are saved as drafts under Agent activity → Artifacts for you to send.',
        'Every step she took is listed under Agent activity → Runs.',
      ],
    },
  ],
};

const TOOLS: ToolGuide[] = [
  {
    name: 'Cyber',
    dropIn: 'Cyber policies, quotes, renewal paperwork, ransomware supplementals or security questionnaires.',
    picks: ['Benchmark of a single policy', 'Renewal comparison (prior term vs renewal)', 'Comparison of competing quotes', 'War and systemic-event exclusion check', 'Ransomware coverage conditions (MFA, backups, EDR)'],
  },
  {
    name: 'D&O',
    dropIn: 'A D&O policy or program, prior and renewal terms, financial statements.',
    picks: ['Benchmark of the program', 'Renewal comparison', 'Financial distress scan: how the program holds up in an insolvency', 'Emerging exposures: how the form treats AI, cyber and crypto wording'],
  },
  {
    name: 'Home Insurance',
    dropIn: 'Homeowners, condo, renters, high-value home or dwelling-fire/rental policies and quotes.',
    picks: ['Benchmark for owner, tenant/condo, high-net-worth or rental dwellings', 'Comparison of competing quotes', 'Renewal comparison'],
  },
  {
    name: 'Personal Lines',
    dropIn: 'Personal auto or personal umbrella policies and quotes.',
    picks: ['Benchmark', 'Comparison of competing quotes', 'Renewal comparison'],
  },
  {
    name: 'Commercial Lines (P&C)',
    dropIn: 'A commercial program: GL, property, commercial auto, inland marine, excess/umbrella, package or BOP.',
    picks: ['Benchmark for the line it detects', 'Package and BOP quote comparisons, including a package quote against a BOP quote', 'Package and BOP renewal comparisons', 'New York construction action-over review across contract, GL, workers comp and excess'],
  },
  {
    name: 'Coverage & Claims Analysis',
    dropIn: 'A policy or program, plus a claim, loss, clause or fact pattern, or a carrier\'s letter.',
    picks: ['Coverage determination: how one policy reads against the facts', 'Program determination: which policies respond, in what order', 'Carrier position audit: what a denial or reservation of rights says, cites and leaves out'],
  },
  {
    name: 'Notice Trigger Registers',
    dropIn: 'A policy, before anything has happened.',
    picks: ['A reference of every notice requirement and deadline, for property & liability, management & professional, or first-party & specialty lines'],
  },
  {
    name: 'Requirements Consulting',
    dropIn: 'A contract\'s insurance requirements, with or without the insurance program or certificates delivered against them.',
    picks: ['Review of the requirements themselves', 'Alignment of your client\'s own program against requirements they were given', 'Verification that a counterparty\'s insurance meets the requirements you set'],
  },
  {
    name: 'Premium Development',
    dropIn: 'Rating worksheets, quotes or policies with premium detail, for two terms where possible.',
    picks: ['A breakdown of what moved the premium, for workers comp, professional liability, GL, property or package/BOP'],
  },
  {
    name: 'Loss Runs',
    dropIn: 'Loss runs from one or more carriers.',
    picks: ['Claim-level fact extraction', 'From a past extraction: a workers comp stewardship report for the client or an underwriter presentation'],
  },
  {
    name: 'Real Estate',
    dropIn: 'Property or liability schedules, leases and contracts, construction budgets, procurement plans.',
    picks: ['Portfolio schedule and forms extraction', 'Contract risk-transfer review', "Builder's risk financials", 'Transit and off-site storage values from procurement plans', 'A portfolio brief that pulls earlier extractions together'],
  },
];

const TOOL_BASICS: string[] = [
  'Open a customer folder, choose Run Workflow, and pick the tool for the line of business.',
  'Select the documents and add a sentence about what you need. RiskNinja reads them and picks the right analysis; your sentence outweighs its guess, so say "compare these quotes" or "check the ransomware conditions" when you know what you want.',
  'Some analyses need more than one document: a renewal comparison needs both terms, and a quote comparison needs at least two quotes.',
  'Reports run in the background and appear on the folder\'s Reports tab. Citations link to the exact spot in the source document.',
  'To pick an exact analysis yourself, switch Workflow view to Dev in your account menu. Every individual tool is listed there. Switch back to Prod any time.',
  'The earlier tools (year-over-year, coverage analysis, document comparison and others) are still under Legacy.',
];

const chevron = (expanded: boolean) => (
  <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const Row: React.FC<{ title: string; expanded: boolean; onToggle: () => void; children: React.ReactNode }> = ({ title, expanded, onToggle, children }) => (
  <div>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="w-full flex items-center justify-between py-2.5 text-left text-sm font-medium text-secondary dark:text-dark-text hover:text-primary"
    >
      {title}
      {chevron(expanded)}
    </button>
    {expanded && <div className="pb-3">{children}</div>}
  </div>
);

const bullets = (points: string[]) => (
  <ul className="pl-5 list-disc space-y-1.5 text-sm text-gray-700 dark:text-gray-300">
    {points.map((p, i) => (
      <li key={i}>{p}</li>
    ))}
  </ul>
);

type TabId = 'chat' | 'email' | 'tools';

const UserGuide: React.FC = () => {
  const [tab, setTab] = useState<TabId>('chat');
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (key: string) => setOpen((cur) => (cur === key ? null : key));
  const guide = tab === 'chat' ? ARIE_CHAT : tab === 'email' ? EMAIL : null;

  return (
    <div className="mx-4 mb-6 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-5">
      <h2 className="text-lg font-semibold text-secondary dark:text-dark-text">User guide</h2>
      <p className="text-sm text-accent dark:text-dark-muted mt-1">How to get the most out of Arie and the automatic tools.</p>

      <div className="flex flex-wrap gap-2 mt-4" role="tablist">
        {([
          ['chat', 'Arie chat'],
          ['email', 'Emailing Arie'],
          ['tools', 'Automatic tools'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => {
              setTab(id);
              setOpen(null);
            }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tab === id ? 'bg-[#1993e5] text-white' : 'border border-gray-200 dark:border-dark-border text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-border'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {guide ? (
        <>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-4">{guide.intro}</p>
          <div className="mt-3 divide-y divide-gray-100 dark:divide-dark-border border-y border-gray-100 dark:border-dark-border">
            {guide.topics.map((t) => (
              <Row key={t.title} title={t.title} expanded={open === t.title} onToggle={() => toggle(t.title)}>
                {bullets(t.points)}
              </Row>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="mt-4">{bullets(TOOL_BASICS)}</div>
          <h3 className="text-sm font-semibold text-secondary dark:text-dark-text mt-5 mb-2">The tools</h3>
          <div className="divide-y divide-gray-100 dark:divide-dark-border border-y border-gray-100 dark:border-dark-border">
            {TOOLS.map((t) => (
              <Row key={t.name} title={t.name} expanded={open === t.name} onToggle={() => toggle(t.name)}>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1.5">
                  <span className="font-medium">Drop in:</span> {t.dropIn}
                </p>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">It picks from:</p>
                {bullets(t.picks)}
              </Row>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default UserGuide;
